import { CSSProperties, useMemo } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import classNames from 'classnames';

import { LinkProps } from './link.types';

import styles from './link.module.css';
import { isDefined } from '@/utils/is-defined';

const Link = ({
  applyColorsToSvgs,
  textColor,
  lightModeTextColor = 'var(--gray-100)',
  hoverTextColor,
  focusTextColor,
  focusBackgroundColor,
  applyFocusStatesToAllChildren,
  applyHoverStatesToAllChildren,
  backgroundColor,
  hoverBackgroundColor,
  className,
  height,
  width,
  borderRadius,
  style,
  fontFamily,
  design,
  designSize,
  fixedState,
  hoverOpacity,
  disabled,
  disabledColor,
  ...rest
}: LinkProps) => {
  const stylesDefined = useMemo(
    () => ({
      height: isDefined(height),
      width: isDefined(width),
      hoverOpactiy: isDefined(hoverOpacity),
    }),
    [height, width, hoverOpacity]
  );

  const globalDesignClasses = useMemo(
    () => ({
      /* these are not module classes, they are global classes */
      ...(design ? { [design]: design } : {}),
      ...(designSize ? { [`${designSize}-global-link`]: designSize } : {}),
      ...(fixedState ? { [`${fixedState}-global-psuedo-state`]: fixedState } : {}),
    }),
    [design, designSize, fixedState]
  );

  return (
    <ReactRouterLink
      {...rest}
      className={classNames(
        styles.link,
        {
          [styles['apply-colors-to-svg']]: applyColorsToSvgs,
          [styles['no-text-color']]: textColor === 'none',
          [styles['light-mode-text-color']]: lightModeTextColor && lightModeTextColor !== 'none',
          [styles['apply-focus-states-to-all-childern']]: !!applyFocusStatesToAllChildren,
          [styles['apply-hover-states-to-all-childern']]: !!applyHoverStatesToAllChildren,
          [styles.height]: stylesDefined.height,
          [styles.width]: stylesDefined.width,
          [styles.disabled]: !!disabled,
          ...globalDesignClasses,
        },
        className
      )}
      style={
        {
          ...(textColor ? { '--link-text-color': textColor } : {}),
          ...(lightModeTextColor ? { '--link-light-mode-text-color': lightModeTextColor } : {}),
          ...(hoverTextColor || textColor ? { '--link-hover-text-color': hoverTextColor || textColor } : {}),
          ...(focusTextColor || hoverTextColor || textColor
            ? { '--link-focus-text-color': focusTextColor || hoverTextColor || textColor }
            : {}),
          ...(backgroundColor ? { '--link-background-color': backgroundColor } : {}),
          ...(hoverBackgroundColor ? { '--link-hover-background-color': hoverBackgroundColor } : {}),
          ...(focusBackgroundColor || hoverBackgroundColor
            ? { '--link-focus-background-color': focusBackgroundColor || hoverBackgroundColor }
            : {}),
          ...(stylesDefined.height ? { '--link-height': height } : {}),
          ...(stylesDefined.width ? { '--link-width': width } : {}),
          ...(borderRadius ? { '--link-border-radius': borderRadius } : {}),
          ...(fontFamily ? { '--link-font-family': fontFamily } : {}),
          ...(stylesDefined.hoverOpactiy ? { '--link-hover-opacity': hoverOpacity } : {}),
          ...(disabledColor ? { '--link-disabled-color': disabledColor } : {}),
          ...style,
        } as CSSProperties
      }
    />
  );
};

export default Link;
